import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { SERVICES } from "../../data/content";
import { ChapterHeading } from "./ChapterHeading";
import { Reveal } from "./Reveal";
import { discussService } from "../../lib/scroll";

const ServiceModal = ({ service, onClose }) => {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <motion.div
            data-testid="service-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-[#07090E]/85 backdrop-blur-md"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={service.title}
        >
            <motion.div
                initial={{ y: 60, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 40, opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full sm:max-w-xl bg-[#0D131F] border border-white/12 rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-h-[88vh] overflow-y-auto shadow-[0_30px_90px_-20px_rgba(0,0,0,0.8)]"
            >
                <button
                    data-testid="service-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 h-9 w-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#00E5FF]/50 transition-colors"
                >
                    <X size={16} />
                </button>

                <div className="h-12 w-12 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center mb-5">
                    <service.icon size={22} className="text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-white pr-10">
                    {service.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{service.desc}</p>

                <p className="mt-6 font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518]">
                    What you get
                </p>
                <ul className="mt-3 space-y-2.5">
                    {service.points.map((p) => (
                        <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <Check size={15} className="mt-0.5 shrink-0 text-[#F5C518]" />
                            {p}
                        </li>
                    ))}
                </ul>

                <p className="mt-6 rounded-xl border border-white/8 bg-[#0A0E17] px-4 py-3 text-xs text-slate-400 leading-relaxed">
                    <span className="text-slate-200 font-medium">Good for: </span>
                    {service.goodFor}
                </p>

                <button
                    data-testid="service-modal-cta"
                    onClick={() => {
                        onClose();
                        discussService(service.title);
                    }}
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5C518] px-6 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] active:scale-[0.98]"
                >
                    Discuss This Service
                    <ArrowRight size={16} />
                </button>
            </motion.div>
        </motion.div>
    );
};

export const Services = () => {
    const [active, setActive] = useState(null);

    return (
        <section id="services" data-testid="services-section" className="py-24 lg:py-32 relative">
            <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
                <ChapterHeading
                    label="Our Services"
                    title="Everything Your Business Needs to Go Digital"
                    sub="From your first website to complete business automation, we build practical digital solutions around your business."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.title} delay={(i % 3) * 0.08}>
                            <div
                                data-testid={`service-card-${i}`}
                                className="group h-full rounded-2xl border border-white/8 bg-[#0D131F] p-6 flex flex-col transition-all duration-300 hover:border-[#00E5FF]/45 hover:bg-[#131C2E] hover:-translate-y-1 hover:shadow-[0_18px_45px_-18px_rgba(0,229,255,0.25)]"
                            >
                                <div className="h-11 w-11 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110">
                                    <s.icon size={20} className="text-[#00E5FF]" />
                                </div>
                                <h3 className="font-display text-lg font-semibold text-white leading-snug">
                                    {s.title}
                                </h3>
                                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed flex-1">
                                    {s.desc}
                                </p>
                                <button
                                    data-testid={`service-learn-more-${i}`}
                                    onClick={() => setActive(s)}
                                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#F5C518] hover:gap-3 transition-all duration-300 self-start"
                                >
                                    Learn More <ArrowRight size={15} />
                                </button>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active && <ServiceModal service={active} onClose={() => setActive(null)} />}
            </AnimatePresence>
        </section>
    );
};
